/**
 * The tile strip above the client lists. Tiles with a `flag` are clickable:
 * they filter the list, and clicking an active tile clears the filter again.
 */
import { useMemo } from 'react';
import { useApp } from '../store/AppContext.jsx';
import { seenWithin } from '../domain/clientReport.js';
import Icon from '../lib/icons.jsx';

export default function StatCards() {
  const { stats, mrrParts, fmtMoney, setSheet, rows, derive } = useApp();

  /*
   * The tiles always count today.
   *
   * The window switch lives in the popup they open, not here: the strip
   * is the page's headline and a control on it invites somebody to
   * change the headline and then forget they did. Today is the question
   * people have when they arrive; the other spans are one click away,
   * inside the list where they are being used.
   */
  const days = 1;

  /*
   * Counted here rather than read off `stats`, which holds one fixed
   * seven-day figure. Today is the question people arrive with, and both
   * halves come off the same walk so they cannot disagree: active plus
   * not active is the book.
   */
  const seen = useMemo(() => {
    let on = 0;
    let off = 0;
    (rows || []).forEach(r => {
      const d = derive(r);
      if (d.excluded) return;
      if (seenWithin(d, days)) on += 1; else off += 1;
    });
    return { on, off };
  }, [rows, derive, days]);

  /* Named once, beside the figure it belongs to: the strip has a single
     window and the popup owns the rest. */
  const windowName = 'Today';

  /*
   * Every tile opens on the clients it counted.
   *
   * Five of them used to filter the list underneath instead, which answers
   * the same question by moving the page out from under whoever was reading
   * it - and the other three answered nothing at all, being figures with no
   * way through to what they were made of.
   */
  const tiles = [
    { m: 'total', n: stats.total, l: 'Clients', i: 'users', c: 'g-purple' },
    /* Signed in this week, not "the status column says Active". The second
       is 1,298 of 1,314 — a number that cannot move, and therefore one
       nobody can act on. See TILE.active. */
    { m: 'active', n: seen.on, l: `Active · ${windowName}`, i: 'zap', c: 'g-green' },
    /* The other half of the same walk. A book that is mostly free is
       mostly quiet, and which accounts have gone quiet is the list
       somebody actually works from. */
    { m: 'inactive', n: seen.off, l: `Not active · ${windowName}`, i: 'eyeoff', c: 'g-slate' },
    { m: 'trial', n: stats.trial, l: 'In trial', i: 'clock', c: 'g-amber' },
    /* What has been collected, not what has not.
       Unpaid was 1,202 of 1,228 — every free account, none of which was ever
       going to pay — and it sat beside a Payments menu reading 18. The
       question the book is actually asked is who is paying. */
    { m: 'paid', n: stats.paid, l: 'Payment done', i: 'card', c: 'g-green' },
    { m: 'renew', n: stats.renew, l: 'Renewals', i: 'refresh', c: 'g-blue' },
    /* Monthly and annual kept apart. A single MRR figure reads the same
       whether it is nine monthly subscriptions or four annual invoices, and
       the two are collected very differently. Each is shown on its own billing
       basis - what the customer actually hands over, and when - so the labels
       carry /mo and /yr rather than converting one into the other. They are
       therefore not meant to be added; MRR on the Billing page is the figure
       that puts both on a monthly footing. */
    /* How many people the figure is made of, under it. AED 590 could be one
       customer or twenty, and which it is changes what the number means. */
    { m: 'monthly', n: fmtMoney(mrrParts.monthly), l: 'Monthly plans /mo', i: 'refresh', c: 'g-pink',
      s: `${mrrParts.monthlyCount} ${mrrParts.monthlyCount === 1 ? 'client' : 'clients'}` },
    { m: 'annual', n: fmtMoney(mrrParts.annualBilled), l: 'Annual plans /yr', i: 'cal', c: 'g-purple',
      s: `${mrrParts.annualCount} ${mrrParts.annualCount === 1 ? 'client' : 'clients'}` },
  ];

  return (
    <>
      <div className="stats">
      {tiles.map(t => (
        <button
          type="button"
          key={t.l}
          className="stat live"
          onClick={() => setSheet({ kind: 'clientStat', metric: t.m, days })}
        >
          <div className={`ic ${t.c}`}><Icon name={t.i} solid /></div>
          <div>
            <div className="n">{t.n}</div>
            <div className="l">{t.l}</div>
            {t.s ? <div className="s">{t.s}</div> : null}
          </div>
        </button>
      ))}
      </div>
    </>
  );
}

/** The same strip, non-interactive, for the pipeline and billing screens. */
export function TileRow({ tiles }) {
  return (
    <div className="stats">
      {tiles.map(([label, value, icon, tone, onClick, active, note]) => {
        const inside = (
          <>
            <div className={`ic ${tone}`}><Icon name={icon} solid /></div>
            <div>
              <div className="n" style={{ fontSize: String(value).length > 8 ? 19 : 26 }}>{value}</div>
              <div className="l">{label}</div>
              {/* What the figure leaves out. A total that cannot count
                  everything should say so under itself rather than let the
                  number stand for the whole. */}
              {note ? <div className="sub">{note}</div> : null}
            </div>
          </>
        );

        /*
         * A figure that can be opened is a button, and one that cannot is not.
         * A tile reading "Replied 2" with no way through to *which two* is a
         * dead end, but making every tile look clickable when half of them go
         * nowhere is worse than leaving them plain.
         */
        return onClick ? (
          <button type="button" className={`stat live${active ? ' on' : ''}`} key={label} onClick={onClick}>
            {inside}
          </button>
        ) : (
          <div className="stat" key={label} style={{ cursor: 'default' }}>{inside}</div>
        );
      })}
    </div>
  );
}
